import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, Star, MapPin, Users, Play, Eye, Timer, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { supabase } from '@/integrations/supabase/client';
import { format, isAfter, isBefore, parseJSON } from 'date-fns';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import ScorekeeperNav from '@/components/ScorekeeperNav';
import { 
  calculateGameStatus, 
  getGameStatusInfo, 
  getTimeUntilGame, 
  sortGamesByStatus,
  formatGameDateTime
} from '@/utils/gameStatusUtils';

interface GameAssignment {
  id: string;
  game_id: string;
  scorekeeper_id: string;
  backup_position: number;
  status: string;
  approved_at: string;
  game: {
    game_title: string;
    game_date: string;
    game_time: string;
    venue_name?: string;
    home_team?: string;
    away_team?: string;
    game_status?: string;
  };
}

const ScorekeeperAssignments: React.FC = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;
  
  const [assignments, setAssignments] = useState<GameAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadAssignments();
    }
  }, [user]);

  const loadAssignments = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🎯 Loading scorekeeper assignments for user:', user.id);

      // Get approved scorekeeper applications
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('scorekeeper_applications')
        .select('*')
        .eq('scorekeeper_id', user.id)
        .eq('status', 'approved')
        .order('approved_at', { ascending: false });

      if (assignmentsError) {
        console.error('Error loading assignments:', assignmentsError);
        throw assignmentsError;
      }

      if (!assignmentsData || assignmentsData.length === 0) {
        console.log('No assignments found for user');
        setAssignments([]);
        return;
      }

      console.log('Found assignments:', assignmentsData.length);

      // Get game details for each assignment
      const assignmentsWithDetails: GameAssignment[] = [];

      for (const assignment of assignmentsData) {
        try {
          // Try venue_games first
          let gameData = null;
          const { data: venueGame } = await supabase
            .from('venue_games')
            .select('game_title, game_date, game_time, venue_id, home_team_id, away_team_id, game_status')
            .eq('id', assignment.game_id)
            .single();

          if (venueGame) {
            // Get venue name
            let venueName = 'Unknown Venue';
            if (venueGame.venue_id) {
              const { data: venue } = await supabase
                .from('venues')
                .select('venue_name')
                .eq('venue_id', venueGame.venue_id)
                .single();
              
              if (venue) {
                venueName = venue.venue_name;
              }
            }

            // Get team names
            let homeTeam = 'TBD';
            let awayTeam = 'TBD';

            if (venueGame.home_team_id) {
              const { data: team } = await supabase
                .from('teams')
                .select('team_name')
                .eq('team_id', venueGame.home_team_id)
                .single();
              
              if (team) homeTeam = team.team_name;
            }

            if (venueGame.away_team_id) {
              const { data: team } = await supabase
                .from('teams')
                .select('team_name')
                .eq('team_id', venueGame.away_team_id)
                .single();
              
              if (team) awayTeam = team.team_name;
            }

            gameData = {
              game_title: `${homeTeam} vs ${awayTeam}`,
              game_date: venueGame.game_date || '',
              game_time: venueGame.game_time || '',
              venue_name: venueName,
              home_team: homeTeam,
              away_team: awayTeam,
              game_status: venueGame.game_status || 'scheduled'
            };
          }

          // If no venue game found, try regular games table
          if (!gameData) {
            const { data: regularGame } = await supabase
              .from('games')
              .select('game_name, game_date, game_time, home_team_id, away_team_id, game_status')
              .eq('id', assignment.game_id)
              .single();

            if (regularGame) {
              // Get team names for regular games
              let homeTeam = 'TBD';
              let awayTeam = 'TBD';

              if (regularGame.home_team_id) {
                const { data: team } = await supabase
                  .from('teams')
                  .select('team_name')
                  .eq('team_id', regularGame.home_team_id)
                  .single();
                
                if (team) homeTeam = team.team_name;
              }

              if (regularGame.away_team_id) {
                const { data: team } = await supabase
                  .from('teams')
                  .select('team_name')
                  .eq('team_id', regularGame.away_team_id)
                  .single();
                
                if (team) awayTeam = team.team_name;
              }

              gameData = {
                game_title: `${homeTeam} vs ${awayTeam}`,
                game_date: regularGame.game_date || '',
                game_time: regularGame.game_time || '',
                venue_name: 'Regular Game',
                home_team: homeTeam,
                away_team: awayTeam,
                game_status: regularGame.game_status || 'scheduled'
              };
            }
          }

          if (!gameData) {
            gameData = {
              game_title: 'Game Details TBD',
              game_date: '',
              game_time: '',
              venue_name: 'Unknown',
              home_team: 'TBD',
              away_team: 'TBD',
              game_status: 'scheduled'
            };
          }

          assignmentsWithDetails.push({
            ...assignment,
            game: gameData
          });

        } catch (err) {
          console.error('Error processing assignment:', assignment.id, err);
        }
      }

      console.log('Processed assignments with details:', assignmentsWithDetails.length);
      
      // Sort assignments by status and priority
      const sortedAssignments = sortGamesByStatus(assignmentsWithDetails);
      setAssignments(sortedAssignments);

    } catch (error) {
      console.error('Error loading scorekeeper assignments:', error);
      setError(error instanceof Error ? error.message : 'Failed to load assignments');
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const getGameStatus = (assignment: GameAssignment) => {
    return calculateGameStatus(
      assignment.game.game_date, 
      assignment.game.game_time,
      assignment.game.game_status
    );
  };

  const getStatusBadge = (assignment: GameAssignment) => {
    const status = getGameStatus(assignment);
    const statusInfo = getGameStatusInfo(status);
    
    return (
      <Badge className={`${statusInfo.bgColor} ${statusInfo.color}`}>
        {statusInfo.label}
      </Badge>
    );
  };

  const formatGameTime = (time: string) => {
    if (!time) return '';
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      return time;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="hidden md:block">
          <MobileHeader />
        </div>
        <div className="md:hidden">
          <MobileHeader />
        </div>
        <div className="flex items-center justify-center pt-20">
          <div className="text-white">Loading assignments...</div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  // Filter assignments by status with proper typing
  const upcomingGames = assignments.filter(a => getGameStatus(a) === 'upcoming');
  const liveGames = assignments.filter(a => getGameStatus(a) === 'active');
  const needsScoring = assignments.filter(a => getGameStatus(a) === 'needs_scoring');
  const completedGames = assignments.filter(a => getGameStatus(a) === 'completed');
  const expiredGames = assignments.filter(a => getGameStatus(a) === 'expired');

  return (
    <div className="min-h-screen bg-black">
      <div className="hidden md:block">
        <MobileHeader />
      </div>
      <div className="md:hidden">
        <MobileHeader />
      </div>
      <div className="pt-20 pb-24 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Game Assignments</h1>
              <p className="text-gray-400">Manage your scorekeeper game assignments</p>
            </div>
            <Button onClick={loadAssignments} variant="outline">
              Refresh
            </Button>
          </div>

          <ScorekeeperNav />

          {error && (
            <Card className="bg-red-900/20 border-red-500/30 mb-6">
              <CardContent className="p-4">
                <p className="text-red-400">{error}</p>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="upcoming" className="space-y-6">
            <div className="space-y-3">
              <TabsList className="grid w-full grid-cols-2 grid-rows-2 gap-3 bg-gray-800 border border-gray-700 p-3 h-auto">
                <TabsTrigger 
                  value="upcoming" 
                  className="text-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center justify-center gap-2 h-12 rounded-lg"
                >
                  Upcoming Games ({upcomingGames.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="completed" 
                  className="text-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center justify-center gap-2 h-12 rounded-lg"
                >
                  Completed Games ({completedGames.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="live" 
                  className="text-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center justify-center gap-2 h-12 rounded-lg"
                >
                  Live Games ({liveGames.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="scoring" 
                  className="text-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center justify-center gap-2 h-12 rounded-lg"
                >
                  Needs Scoring ({needsScoring.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsList className="grid w-full grid-cols-2 gap-3 bg-gray-800 border border-gray-700 p-3 h-auto">
                <TabsTrigger 
                  value="expired" 
                  className="text-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center justify-center gap-2 h-12 rounded-lg"
                >
                  Scoring ({expiredGames.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="all" 
                  className="text-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center justify-center gap-2 h-12 rounded-lg"
                >
                  All ({assignments.length})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="upcoming" className="space-y-4">
              <AssignmentsList assignments={upcomingGames} emptyMessage="No upcoming games" />
            </TabsContent>

            <TabsContent value="live" className="space-y-4">
              <AssignmentsList assignments={liveGames} emptyMessage="No live games" />
            </TabsContent>

            <TabsContent value="scoring" className="space-y-4">
              <AssignmentsList assignments={needsScoring} emptyMessage="No games need scoring" />
            </TabsContent>

            <TabsContent value="completed" className="space-y-4">
              <AssignmentsList assignments={completedGames} emptyMessage="No completed games" />
            </TabsContent>

            <TabsContent value="expired" className="space-y-4">
              <AssignmentsList assignments={expiredGames} emptyMessage="No expired games" />
            </TabsContent>

            <TabsContent value="all" className="space-y-4">
              <AssignmentsList assignments={assignments} emptyMessage="No game assignments found" />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

const AssignmentsList: React.FC<{ assignments: GameAssignment[]; emptyMessage: string }> = ({
  assignments,
  emptyMessage
}) => {
  const formatGameTime = (time: string) => {
    if (!time) return '';
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      return time;
    }
  };

  const getGameStatus = (assignment: GameAssignment) => {
    return calculateGameStatus(
      assignment.game.game_date, 
      assignment.game.game_time,
      assignment.game.game_status
    );
  };

  const getStatusBadge = (assignment: GameAssignment) => {
    const status = getGameStatus(assignment);
    const statusInfo = getGameStatusInfo(status);
    
    return (
      <Badge className={`${statusInfo.bgColor} ${statusInfo.color}`}>
        {statusInfo.label}
      </Badge>
    );
  };

  const getActionButton = (assignment: GameAssignment) => {
    const status = getGameStatus(assignment);
    const statusInfo = getGameStatusInfo(status);
    
    if (statusInfo.canScore) {
      return (
        <Link to={`/live-scoring/${assignment.game_id}`}>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
            <Play className="h-4 w-4 mr-1" />
            {status === 'active' ? 'Live Score' : 'Score Game'}
          </Button>
        </Link>
      );
    }
    
    if (status === 'upcoming') {
      const timeUntil = getTimeUntilGame(assignment.game.game_date, assignment.game.game_time);
      return (
        <Button size="sm" variant="outline" disabled>
          <Timer className="h-4 w-4 mr-1" />
          {timeUntil}
        </Button>
      );
    }
    
    if (status === 'completed') {
      return (
        <Link to={`/live-scoring/${assignment.game_id}`}>
          <Button size="sm" variant="outline">
            <Eye className="h-4 w-4 mr-1" />
            View Scores
          </Button>
        </Link>
      );
    }
    
    if (status === 'expired') {
      return (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <AlertCircle className="h-3 w-3" />
          Expired
        </div>
      );
    }
    
    return null;
  };

  if (assignments.length === 0) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-8 text-center">
          <Star className="h-12 w-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {assignments.map((assignment) => (
        <Card key={assignment.id} className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white">
                {assignment.game.game_title}
              </CardTitle>
              <Star className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(assignment)}
              <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                Position #{assignment.backup_position}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {assignment.game.game_date && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-300">
                  <Calendar className="h-4 w-4" />
                  {formatGameDateTime(assignment.game.game_date, assignment.game.game_time)}
                </div>
              </div>
            )}
            
            {assignment.game.venue_name && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <MapPin className="h-4 w-4" />
                {assignment.game.venue_name}
              </div>
            )}

            {assignment.game.home_team && assignment.game.away_team && (
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Users className="h-4 w-4" />
                {assignment.game.home_team} vs {assignment.game.away_team}
              </div>
            )}
            
            <div className="text-sm text-gray-400">
              <strong>Approved:</strong> {format(new Date(assignment.approved_at), 'MMM dd, yyyy')}
            </div>

            <div className="pt-2">
              {getActionButton(assignment)}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ScorekeeperAssignments;
